import { useState, type ChangeEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Alert, Badge, Button, Card, CardBody, Input, LoadingState } from "@agentnative/ui";
import { apiErrorMessage } from "../lib/api/client";
import {
  LICENSE_STATUS_LABELS,
  convertDesignPartnerLicense,
  getLicense,
  isDesignPartner,
  licenseBadgeVariant,
} from "../lib/api/license";

/**
 * Staff: Convert Design Partner License.
 *
 * Real operational tool -- calls POST
 * /v1/licenses/{license_id}/convert-design-partner against the live
 * backend. This is an explicit, manual admin action (never automatic on a
 * trial-end date -- see license.proto's comment on
 * ConvertDesignPartnerLicense), so this page requires a discount_percent
 * input and an explicit confirm click.
 */
export function AdminConvertLicenseRoute() {
  const { licenseId } = useParams({ from: "/admin/licenses/$licenseId/convert" });
  const [discountPercent, setDiscountPercent] = useState("0");
  const [confirmed, setConfirmed] = useState(false);

  const licenseQuery = useQuery({
    queryKey: ["license", licenseId],
    queryFn: () => getLicense(licenseId),
  });

  const mutation = useMutation({
    mutationFn: () => convertDesignPartnerLicense(licenseId, Number(discountPercent)),
  });

  if (licenseQuery.isLoading) {
    return <LoadingState message="Loading license..." />;
  }

  if (licenseQuery.isError) {
    return (
      <Alert variant="danger" title="Failed to load license">
        {apiErrorMessage(licenseQuery.error)}
      </Alert>
    );
  }

  const license = mutation.data?.license ?? licenseQuery.data!.license;
  const discountValue = Number(discountPercent);
  const discountValid =
    Number.isInteger(discountValue) && discountValue >= 0 && discountValue <= 100;

  return (
    <div className="max-w-xl space-y-6">
      <h1 className="text-2xl font-semibold">Staff: Convert Design Partner License</h1>

      <Card padding="lg">
        <CardBody>
          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt className="text-xs uppercase text-[var(--color-neutral-500)]">License ID</dt>
              <dd className="font-mono text-sm">{license.id}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-[var(--color-neutral-500)]">Organization</dt>
              <dd className="font-mono text-sm">{license.organizationId}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-[var(--color-neutral-500)]">App</dt>
              <dd className="font-mono text-sm">{license.appId}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-[var(--color-neutral-500)]">Status</dt>
              <dd>
                <Badge variant={licenseBadgeVariant(license.status)}>
                  {LICENSE_STATUS_LABELS[license.status]}
                </Badge>
              </dd>
            </div>
          </dl>
        </CardBody>
      </Card>

      {mutation.isSuccess && (
        <Alert variant="success" title="License converted">
          License converted with a {mutation.data.license.designPartnerDiscountPercent}% discount.
        </Alert>
      )}
      {mutation.isError && (
        <Alert variant="danger" title="Failed to convert license">
          {apiErrorMessage(mutation.error)}
        </Alert>
      )}

      {!isDesignPartner(license) ? (
        <Alert variant="warning" title="Not a design-partner license">
          This license is type {license.licenseType}, not LICENSE_TYPE_DESIGN_PARTNER. Only
          design-partner licenses can be converted; the backend will reject this with
          FailedPrecondition if attempted.
        </Alert>
      ) : license.designPartnerDiscountPercent > 0 ? (
        <Alert variant="info" title="Already converted">
          This license already has a {license.designPartnerDiscountPercent}% discount applied.
        </Alert>
      ) : (
        <Card padding="lg">
          <CardBody>
            <p className="mb-4 text-sm text-[var(--color-neutral-600)]">
              Converting is a manual, one-time action -- it is never triggered automatically by
              the trial end date. Set the discount percentage the design partner will receive
              going forward (0-100), then confirm.
            </p>
            <Input
              label="Discount Percent"
              type="number"
              min={0}
              max={100}
              value={discountPercent}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setDiscountPercent(e.target.value)}
              error={!discountValid ? "Must be an integer between 0 and 100" : undefined}
            />
            <label className="mt-4 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              I confirm I want to convert this design-partner license with a {discountPercent}%
              discount.
            </label>
            <Button
              className="mt-4"
              variant="primary"
              loading={mutation.isPending}
              disabled={!discountValid || !confirmed}
              onClick={() => mutation.mutate()}
            >
              Convert License
            </Button>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
